// Base Imports
import React, { useEffect } from 'react'
import Link from 'next/link'
import type { GetStaticPropsContext } from 'next'
import dynamic from 'next/dynamic'
import { removePrecedingSlash } from '@framework/utils/app-util'

// Other Imports
import commerce from '@lib/api/commerce'
import {
  HOMEPAGE_SLUG,
  ABOUT_US_PAGE_DEFAULT_SLUG,
} from '@components/utils/constants'
import withDataLayer, { PAGE_TYPES } from '@components/withDataLayer'
import { EVENTS_MAP } from '@components/services/analytics/constants'
import useAnalytics from '@components/services/analytics/useAnalytics'

const Layout = dynamic(() => import('@components/common/Layout'))
const Loader = dynamic(() => import('@components/ui/LoadingDots'))

import NextHead from 'next/head'
import { useRouter } from 'next/router'
import { PagePropType, getPagePropType } from '@framework/page-props'
import { IPagePropsProvider } from '@framework/contracts/page-props/IPagePropsProvider'
import useEventAnalytics from '@components/services/analytics/useEventAnalytics'
import { AnalyticsEventType } from '@components/services/analytics'
import { useUI } from '@components/ui'
export async function getStaticProps({
  preview,
  locale,
  locales,
}: GetStaticPropsContext) {
  const config = { locale, locales }
  const slugsPromise = commerce.getSlugs({ slug: HOMEPAGE_SLUG })
  const slugs = await slugsPromise
  const infraPromise = commerce.getInfra()
  const infra = await infraPromise

  const pagesPromise = commerce.getAllPages({ config, preview })
  const siteInfoPromise = commerce.getSiteInfo({ config, preview })
  const { pages } = await pagesPromise
  const { categories, brands } = await siteInfoPromise

  // // ---- Display data from content entries ----
  // const id = "05905b59-84a7-41a3-b992-5137c11f86f7";
  // const PageContentsPromise = commerce.getPageContent({ id: id, slug: slugs?.slug });
  // const pageContents = await PageContentsPromise;

  // ---- Display data from pages ----
  //const pageId = "7c79d1b2-53e1-4435-a64f-8298446f3546";
  const PageContentsPromise = commerce.getPagePreviewContent({
    id: '', //pageId,
    slug: ABOUT_US_PAGE_DEFAULT_SLUG, //slugs?.slug,
    workingVersion: process.env.NODE_ENV === 'production' ? true : true, // TRUE for preview, FALSE for prod.
    channel: 'Web',
  })
  const pageContents = await PageContentsPromise
  const props: IPagePropsProvider = getPagePropType({ type: PagePropType.COMMON }) as IPagePropsProvider
  const commonPageProps = await props.getPageProps({ cookies: {} })
  return {
    props: {
      ...commonPageProps,
      categories,
      brands,
      pages,
      slugs,
      globalSnippets: infra?.snippets ?? [],
      snippets: slugs?.snippets ?? [],
      pageContents: pageContents ?? {},
    },
    revalidate: 60,
  }
}
const PAGE_TYPE = PAGE_TYPES.Home
function About({ slugs, pageContents }: any) {
  const { PageViewed } = EVENTS_MAP.EVENT_TYPES
  const router = useRouter()
  const { cartItems } = useUI()
  const { recordAnalytics } = useEventAnalytics()
  useAnalytics(PageViewed, {
    entity: JSON.stringify({
      id: slugs?.id,
      name: slugs?.name,
      metaTitle: slugs?.metaTitle,
      MetaKeywords: slugs?.metaKeywords,
      MetaDescription: slugs?.metaDescription,
      Slug: slugs?.slug,
      Title: slugs?.title,
      ViewType: slugs?.viewType,
    }),
    entityName: PAGE_TYPE,
    pageTitle: slugs?.title,
    entityType: 'Page',
    entityId: slugs?.id,
    eventType: 'PageViewed',
  })
  pageContents['metatitle'] =
    'About us - Premium & Sustainable Menswear brand - DaMENSCH'
  pageContents['metadescription'] =
    'DaMENSCH is one of the leading menswear brand that offers premium innerwear collection for men. We also offer trendiest & sustainable mens topwear & bottomwear.'

  useEffect(()=>{
    recordAnalytics(AnalyticsEventType.OTHER_PAGE, { type: 'other', data: { cartItems } })
  },[])
  return !pageContents ? (
    <>
      <div className="flex w-full text-center flex-con">
        <Loader />
      </div>
    </>
  ) : (
    <>
      {(pageContents?.metatitle ||
        pageContents?.metadescription ||
        pageContents?.metakeywords) && (
        <>
          <NextHead>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, maximum-scale=1"
            />
            <link
              rel="canonical"
              id="canonical"
              href={
                pageContents?.canonical ||
                'https://www.damensch.com' + router.asPath
              }
            />
            {pageContents?.metatitle && (
              <>
                <title>{pageContents?.metatitle}</title>
                <meta name="title" content={pageContents?.metatitle} />
              </>
            )}

            {pageContents?.metadescription && (
              <meta
                name="description"
                content={pageContents?.metadescription}
              />
            )}

            {pageContents?.metakeywords && (
              <meta name="keywords" content={pageContents?.metakeywords} />
            )}
            <meta property="og:image" content={pageContents?.image} />
            {pageContents?.metatitle && (
              <meta
                property="og:title"
                content={pageContents?.metatitle}
                key="ogtitle"
              />
            )}
            {pageContents?.metadescription && (
              <meta
                property="og:description"
                content={pageContents?.metadescription}
                key="ogdesc"
              />
            )}
          </NextHead>
        </>
      )}
      <section className="text-gray-900">
        {/* section first */}
        {pageContents?.fashionthink?.map((fthink: any, fthiid: number) => (
          <>
            <div className="relative w-full about-banner" key={fthiid}>
              <div className="image-about-banner">
                <img
                  placeholder="blur"
                  src={fthink?.fashionthink_image}
                  alt="about banner"
                  className="w-full"
                />
              </div>
              <div className="absolute px-6 about-text top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4 sm-top-auto sm:bottom-16 sm:left-0 sm:translate-y-0 sm:translate-x-0 ">
                <h1 className="text-5xl font-bold leading-normal text-orange sm:text-8xl text-heading-width">
                  {fthink.fashionthink_title}
                </h1>
                <div className="hidden mt-4 sm:grid sm:grid-cols-2 sm:gap-4">
                  <div
                    className="text-white"
                    dangerouslySetInnerHTML={{
                      __html: fthink?.fashionthink_description1,
                    }}
                  ></div>
                  <div
                    className="text-white"
                    dangerouslySetInnerHTML={{
                      __html: fthink?.fashionthink_description2,
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="flex flex-col w-full p-6 bg-black sm:hidden">
              <div
                className="mb-4 text-white"
                dangerouslySetInnerHTML={{
                  __html: fthink?.fashionthink_description1,
                }}
              ></div>
              <div
                className="text-white"
                dangerouslySetInnerHTML={{
                  __html: fthink?.fashionthink_description2,
                }}
              ></div>
            </div>
            {/* section first */}
          </>
        ))}

        {/* second section */}
        {pageContents?.thinksustainability?.map((tdata: any, tdid: number) => (
          <div className="relative w-full about-banner" key={tdid}>
            <div className="relative image-about-banner">
              <img
                placeholder="blur"
                src={tdata?.thinksustainability_image}
                alt="about banner"
                className="w-full"
              />
              <h2 className="absolute block w-full text-xl font-bold leading-normal text-center text-white bottom-16 left-2/4 -translate-x-2/4 sm:hidden">
                {tdata?.thinksustainability_title}
              </h2>
            </div>
            <div className="px-6 py-6 bg-black about-text realtive sm:bg-transparent sm:absolute sm:bottom-16 sm:left-0 sm:py-0">
              <h2 className="hidden text-5xl font-bold leading-normal text-white sm:block text-heading-width">
                {tdata?.thinksustainability_title}
              </h2>
              <div className="w-full mt-4 overflow-hidden">
                <div
                  className="text-white max-600-w"
                  dangerouslySetInnerHTML={{
                    __html: tdata?.thinksustainability_description,
                  }}
                ></div>
                <Link
                  legacyBehavior
                  href={`/${removePrecedingSlash(
                    tdata?.thinksustainability_buttonlink
                  )}`}
                  passHref
                >
                  <a
                    href="javascript:void(0)"
                    className="inline-block px-4 py-2 mt-4 text-black bg-white"
                  >
                    {tdata?.thinksustainability_buttontext}
                  </a>
                </Link>
              </div>
            </div>
          </div>
        ))}
        {/* second section */}
        {/* third section */}
        {pageContents?.thinkdesign?.map((thinkdata: any, thiid: number) => (
          <div
            className="flex flex-col-reverse items-center pt-3 bg-black sm:flex-row pb-14"
            key={thiid}
          >
            <div className="w-full px-6 py-6 bg-black about-text sm:w-4/12 sm:bg-transparent sm:py-0">
              <h2 className="hidden text-5xl font-bold leading-normal text-white uppercase sm:block text-heading-width">
                {thinkdata?.thinkdesign_title}
              </h2>
              <div className="w-full mt-4 overflow-hidden">
                <div
                  className="text-white"
                  dangerouslySetInnerHTML={{
                    __html: thinkdata?.thinkdesign_description,
                  }}
                ></div>
                <Link
                  legacyBehavior
                  href={`/${removePrecedingSlash(
                    thinkdata?.thinkdesign_buttonlink
                  )}`}
                  passHref
                >
                  <a
                    href="javascript:void(0)"
                    className="inline-block px-4 py-2 mt-4 text-black bg-white"
                  >
                    {thinkdata?.thinkdesign_buttontext}
                  </a>
                </Link>
              </div>
            </div>
            <div className="relative w-full sm:w-8/12 image-about-banner">
              <img
                placeholder="blur"
                src={thinkdata?.thinkdesign_image}
                alt="about banner"
                className="w-full"
              />
              <h2 className="absolute block text-xl font-bold leading-normal text-black top-5 right-2 sm:hidden">
                {thinkdata?.thinkdesign_title}
              </h2>
              <Link
                legacyBehavior
                href={`/${removePrecedingSlash(
                  thinkdata?.thinkdesign_buttonlink
                )}`}
                passHref
              >
                <a
                  href="javascript:void(0)"
                  className="absolute inline-block px-4 py-2 mt-4 text-white bg-black bottom-4 left-4"
                >
                  {thinkdata?.thinkdesign_buttontext}
                </a>
              </Link>
            </div>
          </div>
        ))}
        {/* third section */}

        {/* fourth section start */}
        {pageContents?.thinkinnovation?.map((indata: any, daid: number) => (
          <div className="relative w-full about-banner" key="daid">
            <div className="relative image-about-banner">
              <img
                placeholder="blur"
                src={indata?.thinkinnovation_image}
                alt="about banner"
                className="w-full"
              />
              <h2 className="absolute block w-full text-xl font-bold leading-normal text-center text-white bottom-16 left-2/4 -translate-x-2/4 sm:hidden">
                {indata?.thinkinnovation_title}
              </h2>
            </div>
            <div className="px-6 py-6 bg-white about-text realtive sm:py-0 sm:bg-transparent sm:absolute sm:top-2/4 sm:-translate-y-2/4 sm:right-0 max-600-w">
              <h2 className="hidden text-5xl font-bold leading-normal text-white uppercase sm:block text-heading-width">
                {indata?.thinkinnovation_title}
              </h2>
              <div className="w-full mt-4 overflow-hidden">
                <div
                  className="text-black sm:text-white"
                  dangerouslySetInnerHTML={{
                    __html: indata?.thinkinnovation_description,
                  }}
                ></div>
                <Link
                  legacyBehavior
                  href={`/${removePrecedingSlash(
                    indata?.thinkinnovation_buttonlink
                  )}`}
                  passHref
                >
                  <a
                    href="javascript:void(0)"
                    className="inline-block px-4 py-2 mt-4 text-white bg-black sm:bg-white sm:text-black"
                  >
                    {indata?.thinkinnovation_buttontext}
                  </a>
                </Link>
              </div>
            </div>
          </div>
        ))}
        {/* fourth section End */}

        {/* fifth section  */}
        <div className="flex flex-row items-center bg-black py-14">
          <div className="px-6 mx-auto about-text max-1024-w">
            {pageContents?.founderheading?.map((fodata: any, foid: number) => (
              <div className="w-full mb-6" key={foid}>
                <h2 className='font-bold text-orange text-5xl leading-normal uppercase relative after:content-[""] after:absolute after:w-28 after:h-1 after:bg-orange-600 after:bottom-0 after:left-0'>
                  {fodata?.founderheading_title}
                </h2>
                <div className="w-full mt-4 overflow-hidden">
                  <p className="text-white">
                    {fodata?.founderheading_subtitle}
                  </p>
                </div>
              </div>
            ))}
            <div className="grid grid-cols-1 mt-4 sm:grid-cols-2 sm:gap-6">
              {pageContents?.profile?.map((prodata: any, pid: number) => (
                <>
                  <div className="w-full" key={pid}>
                    <h2 className="mb-2 text-xl font-bold leading-normal uppercase text-orange sm:text-3xl">
                      {prodata?.profile_title}
                    </h2>
                    <div
                      className="text-white"
                      dangerouslySetInnerHTML={{
                        __html: prodata?.profile_description,
                      }}
                    ></div>
                  </div>
                </>
              ))}
            </div>
          </div>
        </div>
        {/* fifth section  */}
      </section>
    </>
  )
}

About.Layout = Layout

export default withDataLayer(About, PAGE_TYPE)
